/**
 * Canonical rest-pose quaternions for each mannequin model.
 *
 * Rest poses are a property of the MODEL, not of any instance.  They are
 * extracted once from the GLB bind pose and frozen here as the immutable
 * source of truth.  No runtime code should ever capture or derive rest
 * poses from live THREE.js bone quaternions — doing so is fragile and
 * susceptible to race conditions where bones have already been posed.
 *
 * ## Keeping in sync with the GLB
 *
 * This file is auto-generated by build-mannequin.py.  Do not edit manually.
 *
 * ## Format
 *
 * All quaternions are [x, y, z, w] — matching THREE.js and glTF spec.
 */

export type Q4 = readonly [number, number, number, number];

// ---------------------------------------------------------------------------
// Male mannequin — extracted from neutral-rigged.fbx bind pose
// ---------------------------------------------------------------------------

const MALE_REST_POSE_DATA: Record<string, Q4> = {
  root: [Math.SQRT1_2, -1e-6, -1e-6, Math.SQRT1_2],
  hips: [-0.039415, 1e-6, 0.0, 0.999223],
  spine: [0.035567, -0.0, -0.0, 0.999367],
  spine1: [0.027379, -0.0, -0.0, 0.999625],
  neck: [0.111981, -0.0, -0.0, 0.99371],
  head: [-0.135331, 0.0, 0.0, 0.9908],

  arm_twistL: [0.0, 0.0, -0.0, 1.0],
  arm_twistR: [0.0, -0.0, 0.0, 1.0],
  footL: [0.389292, 0.547451, -0.429676, 0.60343],
  footR: [0.389292, -0.547451, 0.429676, 0.60343],
  forearmL: [-0.0, 0.0, -0.373691, 0.927553],
  forearmR: [-0.0, -0.0, 0.373691, 0.927553],
  forearm_twistL: [0.0, -0.01849, -0.0, 0.999829],
  forearm_twistR: [0.0, 0.01849, 0.0, 0.999829],
  handL: [-0.062404, -0.019399, 0.014098, 0.997763],
  handR: [-0.062404, 0.019399, -0.014098, 0.997763],
  leg_twistL: [0.0, 0.0, -0.0, 1.0],
  leg_twistR: [0.0, -0.0, 0.0, 1.0],
  shinL: [1e-6, -0.0, 0.038401, 0.999262],
  shinR: [1e-6, 0.0, -0.038401, 0.999262],
  shoulderL: [-0.512096, -0.512096, -0.487604, 0.487604],
  shoulderR: [-0.512096, 0.512096, 0.487604, 0.487604],
  thighL: [-0.695494, -0.045237, -0.71686, 0.018802],
  thighR: [-0.695494, 0.045237, 0.71686, 0.018802],
  thigh_twistL: [0.0, -0.0, 0.0, 1.0],
  thigh_twistR: [0.0, 0.0, -0.0, 1.0],
  toeL: [0.0, -0.999946, -0.01037, 1.8e-5],
  toeR: [0.0, 0.999946, 0.01037, 1.8e-5],
  upper_armL: [-0.379888, -0.001133, -0.020904, 0.924796],
  upper_armR: [-0.379888, 0.001133, 0.020904, 0.924796],

  finger_indexL1: [-0.063321, -0.007749, -0.121236, 0.990572],
  finger_indexL2: [-0.096542, -0.022799, 0.011597, 0.995],
  finger_indexL3: [-0.022538, -0.004976, 0.002984, 0.999729],
  finger_indexR1: [-0.063319, 0.007749, 0.121239, 0.990571],
  finger_indexR2: [-0.096541, 0.022791, -0.011603, 0.995],
  finger_indexR3: [-0.022549, 0.005002, -0.002981, 0.999729],
  finger_middleL1: [-0.036565, 0.000659, 0.018055, 0.999168],
  finger_middleL2: [-0.107914, 0.001393, -0.023093, 0.993891],
  finger_middleL3: [-0.016762, -0.000157, 0.00089, 0.999859],
  finger_middleR1: [-0.036566, -0.000657, -0.018057, 0.999168],
  finger_middleR2: [-0.107916, -0.001391, 0.023094, 0.993891],
  finger_middleR3: [-0.016758, 0.000151, -0.000888, 0.999859],
  finger_pinkyL1: [-0.088229, 0.017106, 0.189478, 0.977763],
  finger_pinkyL2: [-0.114142, 0.038186, -0.059442, 0.990949],
  finger_pinkyL3: [-0.027161, 0.006841, -0.016127, 0.999478],
  finger_pinkyR1: [-0.088237, -0.017086, -0.189481, 0.977762],
  finger_pinkyR2: [-0.114126, -0.038212, 0.059438, 0.99095],
  finger_pinkyR3: [-0.027161, -0.006836, 0.016131, 0.999478],
  finger_ringL1: [-0.043105, 0.004761, 0.10954, 0.993036],
  finger_ringL2: [-0.100378, 0.019161, -0.030859, 0.994286],
  finger_ringL3: [-0.005664, 0.00088, -0.004153, 0.999975],
  finger_ringR1: [-0.043106, -0.004757, -0.10954, 0.993036],
  finger_ringR2: [-0.100376, -0.019162, 0.03086, 0.994286],
  finger_ringR3: [-0.005666, -0.000881, 0.004149, 0.999975],
  finger_thumbL1: [-0.108934, 0.138414, -0.363634, 0.914738],
  finger_thumbL2: [-0.096084, -0.012209, -0.00637, 0.995278],
  finger_thumbL3: [-0.004544, 0.074048, 0.048687, 0.996055],
  finger_thumbR1: [-0.108934, -0.138412, 0.363634, 0.914738],
  finger_thumbR2: [-0.096086, 0.01221, 0.006371, 0.995278],
  finger_thumbR3: [-0.00454, -0.074055, -0.048691, 0.996054],
};

const MALE_REST_POSE: Readonly<Record<string, Q4>> =
  Object.freeze(MALE_REST_POSE_DATA);

// ---------------------------------------------------------------------------
// Exported per-model rest pose map
// ---------------------------------------------------------------------------

const _maleRestMap = new Map(Object.entries(MALE_REST_POSE));

export const MODEL_REST_POSES: Record<string, ReadonlyMap<string, Q4>> = {
  "male-neutral": _maleRestMap,
  "male-athletic": _maleRestMap,
  "male-muscular": _maleRestMap,
  "male-skinny": _maleRestMap,
};
